// ─────────────────────────────────────────────────────────────────────────────
// WorkshopTab.jsx  — action-focus layout
// Left column  : phase bar + current action card + up-next list
// Right sidebar: repair progress timeline (single source of truth for history)
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useCallback,useEffect } from "react";
import { SectionCard, formatINR, formatDateTime } from "../../UI/index";
import { STATUS_TO_ACTION, STATUSES_AT_OR_PAST_CLAIM_SENT,STATUSES_AT_OR_PAST_ESTIMATE_APPROVED,STATUSES_AT_OR_PAST_SURVEYOR_ASSIGNED,STATUSES_AT_OR_PAST_DISMANTLING_APPROVED,
          STATUSES_AT_OR_PAST_DISMANTLING_COMPLETED,STATUSES_AT_OR_PAST_SECOND_SURVEY,STATUSES_AT_OR_PAST_FINAL_APPROVED,STATUSES_AT_OR_PAST_WORK_STARTED,
          STATUSES_AT_OR_PAST_TRUCK_REPAIRED,STATUSES_AT_OR_PAST_REINSPECTION, UP_NEXT} from "./WorkshopConstants.js";
import {
  StepDismantlingApproval,
  StepDismantlingCompleted,
  StepSecondVisit,
  StepRevisedEstimate,
  StepSecondSurveyDone,
  StepFinalApproval,
  StepWorkStarted,
  StepTruckRepaired,
  StepReinspection,
  StepProforma,
  StepAssessment,
  StepCrossCheck,
  StepTaxInvoice,
  StepPaymentsSettlement,
  StepMarkPaymentsReceived,
  StepSettleClaim,
  StepCloseCase,
} from "./WorkshopTabHelpers";
import {
  StepWorkshopEstimate,
  StepClaimForm,
  StepAssignWorkshopSurveyor,
  StepAwaitWorkshopSurvey
} from "./WorkshopIntakesteps";
import { PendingSection } from "../CaseTabs";

// ─── helpers ─────────────────────────────────────────────────────────────────
const STAGE_ORDER = ["CASE_CREATED", "SURVEY_PROCESS", "WORKSHOP_REPAIR", "DELIVERY_PAYMENT", "CLAIM_SETTLEMENT"];
const stageIndex  = s => STAGE_ORDER.indexOf(s);

// Derive which phase the case is currently in (for the phase bar)
const PHASE_ORDER = ["intake", "claim", "repair", "delivery"];
 
function derivePhase({ estimateApproved, claimSent, surveyorAssigned,surveyDone, truckRepaired, reinspectionDone, proformaDone }) {
  if (!estimateApproved) return "intake";
  if (!claimSent || !surveyorAssigned) return "claim";
  if (!surveyDone) return "claim";
  if (!truckRepaired)    return "repair";
  if(!reinspectionDone) return "repair";
  // if(!proformaDone) return "repair";
  return "delivery";
}

// ─── Phase Bar ────────────────────────────────────────────────────────────────
function PhaseBar({ phase }) {
  const phases = [
    { key: "intake",  label: "Intake"          },
    { key: "claim",   label: "Claim & survey"  },
    { key: "repair",  label: "Repair"          },
    { key: "delivery",label: "Delivery"        },
  ];
  const idx = phases.findIndex(p => p.key === phase);

  return (
    <div style={{
      display: "flex", borderRadius: 8, overflow: "hidden",
      border: "1px solid var(--wd-border)", marginBottom: 12,
    }}>
      {phases.map((p, i) => {
        const done   = i < idx;
        const active = i === idx;
        return (
          <div key={p.key} style={{
            flex: 1, textAlign: "center", padding: "6px 4px",
            fontSize: 10, fontWeight: 600,
            textTransform: "uppercase", letterSpacing: ".04em",
            borderLeft: i > 0 ? "1px solid var(--wd-border)" : "none",
            background: done   ? "rgba(34,197,94,.08)"
                      : active ? "rgba(234,179,8,.06)"
                      : "transparent",
            color: done   ? "var(--wd-green)"
                 : active ? "var(--wd-yellow)"
                 : "var(--wd-t3)",
          }}>
            {done ? "✓ " : ""}{p.label}
          </div>
        );
      })}
    </div>
  );
}

// ─── Action Card wrapper ──────────────────────────────────────────────────────
// Wraps each step component in a highlighted "action required" card shell
function ActionCard({ children }) {
  return (
    <div style={{
      borderRadius: 10,
      border: "1px solid rgba(234,179,8,.25)",
      background: "rgba(234,179,8,.04)",
      overflow: "hidden",
    }}>
      {children}
    </div>
  );
}

// ─── Up Next list ─────────────────────────────────────────────────────────────
function UpNextList({ items }) {
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((item, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "flex-start", gap: 8,
          padding: "7px 10px", borderRadius: 7,
          background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
            background: "var(--wd-border)", display: "flex",
            alignItems: "center", justifyContent: "center",
            fontSize: 9, color: "var(--wd-t3)", marginTop: 1,
          }}>
            {i + 1}
          </div>
          <span style={{ fontSize: 11, color: "var(--wd-t3)", lineHeight: 1.5 }}>
            {item}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Section Label ────────────────────────────────────────────────────────────
function SLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".06em", color: "var(--wd-t3)",
      marginBottom: 5, display: "flex", alignItems: "center", gap: 6,
    }}>
      {children}
      <span style={{ flex: 1, height: 1, background: "var(--wd-border)", display: "block" }} />
    </div>
  );
}

// ─── Completed step accordion row ─────────────────────────────────────────────
// Each completed action renders as an expanded pill that collapses on click
// ─── Completed step collapsible row ──────────────────────────────────────────
function CompletedRow({ label, children, initialOpen = true }) {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div style={{
      borderRadius: 8,
      border: "1px solid rgba(34,197,94,.2)",
      background: "rgba(34,197,94,.04)",
      overflow: "hidden",
    }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 12px", textAlign: "left",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wd-green)" strokeWidth="2.5">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-t1)", flex: 1 }}>{label}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20,
          background: "rgba(34,197,94,.12)", color: "var(--wd-green)",
          border: "1px solid rgba(34,197,94,.25)", textTransform: "uppercase",
          letterSpacing: ".04em", marginRight: 4,
        }}>Done</span>
        <span style={{
          fontSize: 11, color: "var(--wd-t3)", display: "inline-block",
          transition: "transform .15s",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }}>▾</span>
      </button>
 
      {open && (
        <div style={{
          borderTop: "1px solid rgba(34,197,94,.15)",
          padding: "0 10px 10px",
          pointerEvents: "auto",
          opacity: 0.82,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function PhaseContent({ phaseKey, activePhase, currentAction, steps, upNextItems }) {
  const phaseIdx  = PHASE_ORDER.indexOf(phaseKey);
  const activeIdx = PHASE_ORDER.indexOf(activePhase);
  const isPast    = phaseIdx < activeIdx;
  const isFuture  = phaseIdx > activeIdx;
 
  // ── Future: locked ────────────────────────────────────────────────────────
  if (isFuture) {
    return (
      <div style={{
        padding: "28px 16px", borderRadius: 10, textAlign: "center",
        background: "var(--wd-hover)", border: "1px dashed var(--wd-border)",
      }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>🔒</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t2)" }}>Not started yet</div>
        <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 3 }}>
          This phase will unlock once the previous phase is complete.
        </div>
      </div>
    );
  }
 
  // ── Past: all steps collapsed ─────────────────────────────────────────────
  if (isPast) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {steps.map(s => (
          <CompletedRow key={s.key} label={s.label}>{s.component}</CompletedRow>
        ))}
        <div style={{
          marginTop: 4, padding: "8px 12px", borderRadius: 8,
          background: "rgba(34,197,94,.06)", border: "1px solid rgba(34,197,94,.18)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wd-green)" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><polyline points="16 8 10 14 7 11" transform="translate(1,1)"/>
          </svg>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--wd-green)" }}>
            Phase complete — all steps done
          </span>
        </div>
      </div>
    );
  }
 
  // ── Current phase ─────────────────────────────────────────────────────────
  const completedHere = steps.filter(s => s.done && s.key !== currentAction);
  const activeStep    = steps.find(s => s.key === currentAction);
  const phaseAllDone  = steps.every(s => s.done);
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
 
      {/* Completed steps within this phase */}
      {completedHere.length > 0 && (
        <>
          <SLabel>Completed</SLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {completedHere.map(s => (
              <CompletedRow key={s.key} label={s.label}>{s.component}</CompletedRow>
            ))}
          </div>
        </>
      )}
 
      {/* Active step */}
      {activeStep && !phaseAllDone && (
        <>
          <SLabel>Action required</SLabel>
          <ActionCard>{activeStep.component}</ActionCard>
        </>
      )}
 
      {/* Phase fully done */}
      {phaseAllDone && (
        <div style={{
          padding: "14px 16px", borderRadius: 10, textAlign: "center",
          background: "rgba(34,197,94,.06)", border: "1px solid rgba(34,197,94,.2)",
        }}>
          <div style={{ fontSize: 20, marginBottom: 4 }}>🎉</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-green)" }}>All steps complete</div>
        </div>
      )}
 
      {/* Up next */}
      {upNextItems?.length > 0 && activeStep && !phaseAllDone && (
        <>
          <SLabel>Up next</SLabel>
          <UpNextList items={upNextItems} />
        </>
      )}
    </div>
  );
}

// ─── Repair Progress Timeline (right sidebar) ─────────────────────────────────
export function RepairTimeline({ caseInfo, workshopSurveyor, paymentsDone, paymentsReceived, claimSettled, caseClosed }) {
  const s = caseInfo.currentStatus || "";
 
  const steps = [
    { label: "Workshop assigned",    date: caseInfo.workshopAssignedDate, done: !!caseInfo.workshopAssignedDate, isNew: false },
    { label: "Vehicle picked up",    date: caseInfo.driverSentDate, done: !!caseInfo.driverSentDate, isNew: false },
    { label: "Vehicle at workshop",  date: caseInfo.vehicleEntryDate, done: !!caseInfo.vehicleEntryDate || STATUSES_AT_OR_PAST_ESTIMATE_APPROVED.includes(s), isNew: false },
    { label: "Estimate approved",    date: caseInfo.estimateApprovedDate, done: !!caseInfo.estimateApprovedDate || STATUSES_AT_OR_PAST_ESTIMATE_APPROVED.includes(s), isNew: true },
    { label: "Claim form uploaded",  date: caseInfo.claimFormDoc, done: !!caseInfo.claimFormDoc || STATUSES_AT_OR_PAST_CLAIM_SENT.includes(s) || ["CLAIM_FORM_SENT_TO_CUSTOMER","CLAIM_FORM_SIGNED","CLAIM_FORM_APPROVED"].includes(s), isNew: true },
    { label: "Customer signed",      date: caseInfo.customerSignedDate, done: ["CLAIM_FORM_SIGNED","CLAIM_FORM_APPROVED"].includes(s) || STATUSES_AT_OR_PAST_CLAIM_SENT.includes(s), isNew: true },
    { label: "Claim sent to insurer",date: caseInfo.claimFormSentDate, done: !!caseInfo.claimFormSentDate || STATUSES_AT_OR_PAST_CLAIM_SENT.includes(s),         isNew: true },
    { label: "Workshop surveyor",    date: caseInfo.workshopSurveyorDate, done: !!workshopSurveyor?.status === "COMPLETED" || STATUSES_AT_OR_PAST_SURVEYOR_ASSIGNED.includes(s), isNew: true },
    { label: "Dismantling approved", date: caseInfo.dismantlingApprovedDate, done: !!caseInfo.dismantlingApprovedDate || STATUSES_AT_OR_PAST_DISMANTLING_APPROVED.includes(s), isNew: false },
    { label: "Dismantling completed",date: caseInfo.dismantlingCompletedDate, done: !!caseInfo.dismantlingCompletedDate || STATUSES_AT_OR_PAST_DISMANTLING_COMPLETED.includes(s), isNew: false },
    { label: "2nd visit assigned",   date: caseInfo.workshopVisit2Date, done: !!caseInfo.workshopVisit2Date || STATUSES_AT_OR_PAST_SECOND_SURVEY.includes(s),    isNew: false },
    { label: "2nd survey done",      date: caseInfo.workshopSurveyCompletedDate, done: !!caseInfo.workshopSurveyCompletedDate || STATUSES_AT_OR_PAST_SECOND_SURVEY.includes(s), isNew: false },
    { label: "Final approval",       date: caseInfo.finalApprovalDate, done: !!caseInfo.finalApprovalDate || STATUSES_AT_OR_PAST_FINAL_APPROVED.includes(s),    isNew: false },
    { label: "Work started",         date: caseInfo.workStartedDate, done: !!caseInfo.workStartedDate || STATUSES_AT_OR_PAST_WORK_STARTED.includes(s),        isNew: false },
    { label: "Truck repaired",       date: caseInfo.truckFinalizedDate, done: !!caseInfo.truckFinalizedDate || STATUSES_AT_OR_PAST_TRUCK_REPAIRED.includes(s),   isNew: false },
    { label: "Reinspection done",    date: caseInfo.readyForInspectionDate, done: !!caseInfo.readyForInspectionDate || STATUSES_AT_OR_PAST_REINSPECTION.includes(s), isNew: false },
    { label: "Proforma uploaded",    date: caseInfo.proformaDoc, done: !!caseInfo.proformaDoc || ["PROFORMA_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(s), isNew: false },
    { label: "Assessment received",  date: caseInfo.assessmentReceivedDate, done: !!caseInfo.assessmentReceivedDate || ["ASSESSMENT_RECEIVED","ASSESSMENT_CROSSCHECKED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(s), isNew: false },
    { label: "Tax invoice received", date: caseInfo.taxInvoiceDoc, done: !!caseInfo.taxInvoiceDoc || ["TAX_INVOICE_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(s), isNew: false },
    { label: "Payments complete",    date: caseInfo.paymentsDone, done: paymentsDone, isNew: false },
     { label:"Payments received",     date: caseInfo.paymentsReceived, done: paymentsReceived },
    { label:"Claim settled",         date: caseInfo.claimSettled, done: claimSettled },
    { label:"Case closed",           date: caseInfo.caseClosed, done: caseClosed },
  ];
 
  const completedCount = steps.filter(s => s.done).length;
  const activeIdx      = steps.findIndex(s => !s.done);
 
  return (
    <SectionCard title={`Repair progress (${completedCount}/${steps.length})`}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {steps.map((step, i) => {
          const isActive = i === activeIdx;
          return (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                <div style={{
                  width: 16, height: 16, borderRadius: "50%", marginTop: 1,
                  background: step.done ? "var(--wd-green)" : isActive ? "transparent" : "var(--wd-hover)",
                  border: step.done
                    ? "2px solid var(--wd-green)"
                    : isActive
                    ? "2px solid var(--wd-yellow)"
                    : "2px solid var(--wd-border-md)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {step.done && (
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div style={{
                    width: 2, height: 16,
                    background: step.done ? "var(--wd-green)" : "var(--wd-border)",
                  }} />
                )}
              </div>
              <div style={{ paddingBottom: 2, display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{
                  fontSize: 11,
                  color: step.done ? "var(--wd-t1)" : isActive ? "var(--wd-yellow)" : "var(--wd-t3)",
                  fontWeight: step.done || isActive ? 500 : 400,
                }}>
                  {step.label}
                </span>
                {step.isNew && (
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: "var(--wd-blue)", display: "inline-block", flexShrink: 0,
                  }} />
                )}
                </div>
                {step.done && step.date && (
                  <span style={{ fontSize: 9, color: "var(--wd-t3)", lineHeight: 1 }}>
                    {new Date(step.date).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

export function WorkshopTab({ workshopInfo, caseInfo, surveys, insuranceDetails, onRefetch, onGoToSurveys }) {
  const isBeforeWorkshop = stageIndex(caseInfo?.stage) < stageIndex("WORKSHOP_REPAIR");
 
  const [localPatch, setLocalPatch] = useState({});
  const [localWorkshopPatch, setLocalWorkshopPatch] = useState({});
  const merged = { ...caseInfo, ...localPatch };
  const mergedWorkshopInfo = { ...workshopInfo, ...localWorkshopPatch };

  useEffect(() => { setLocalPatch({}); }, [caseInfo]);
  useEffect(() => { setLocalWorkshopPatch({}); }, [workshopInfo]);
 
  const handleRefetch = useCallback((patch, workshopPatch) => {
    if (patch) setLocalPatch(prev => ({ ...prev, ...patch }));
    if (workshopPatch) setLocalWorkshopPatch(prev => ({ ...prev, ...workshopPatch }));
    onRefetch?.();
  }, [onRefetch]);
 
  const workshopSurveyor = (surveys || []).find(
    s => s.type === "WORKSHOP_SURVEY" || (s.type || "").toUpperCase().includes("WORKSHOP")
  );

  const customerPayableAmount  = merged?.customerPayableAmount ?? null;
  const customerPaid = customerPayableAmount === 0
  || merged?.customerPaymentStatus === "PAID"
  || (merged?.customerPaidAmount != null && merged?.customerPaidAmount > 0);
  
  const status = merged.currentStatus || ""; 
  const workshopPaid = merged?.workshopPaymentStatus === "PAID";
  const insReceived  = ["PAYMENT_RECEIVED","SETTLED","CASE_CLOSED"].includes(merged?.claimStatus || "");
  const paymentsDone = customerPaid && insReceived && workshopPaid
  || ["PAYMENT_COMPLETED", "CLAIM_SETTLED", "CASE_CLOSED"].includes(status);
  // ── Milestone flags ───────────────────────────────────────────────────────
  
  const estimateApproved  = !!merged.estimateApprovedDate  || STATUSES_AT_OR_PAST_ESTIMATE_APPROVED.includes(status);
  const claimSent         = !!merged.claimFormSentDate     || STATUSES_AT_OR_PAST_CLAIM_SENT.includes(status);
  const surveyorAssigned  = !!workshopSurveyor?.surveyorId || STATUSES_AT_OR_PAST_SURVEYOR_ASSIGNED.includes(status);
  const surveyDone = workshopSurveyor?.status === "COMPLETED" || STATUSES_AT_OR_PAST_DISMANTLING_APPROVED.includes(status);
  const dismantlingApproved  = !!merged.dismantlingApprovedDate  || STATUSES_AT_OR_PAST_DISMANTLING_APPROVED.includes(status);
  const dismantlingCompleted = !!merged.dismantlingCompletedDate || STATUSES_AT_OR_PAST_DISMANTLING_COMPLETED.includes(status);
  const visit2Assigned    = !!merged.workshopVisit2Date          || STATUSES_AT_OR_PAST_SECOND_SURVEY.includes(status);
  const survey2Done       = !!merged.workshopVisit2CompletedDate || STATUSES_AT_OR_PAST_SECOND_SURVEY.includes(status);
  const finalApproved     = !!merged.finalApprovalDate  || STATUSES_AT_OR_PAST_FINAL_APPROVED.includes(status);
  const workStarted       = !!merged.workStartedDate    || STATUSES_AT_OR_PAST_WORK_STARTED.includes(status);
  const truckRepaired     = !!merged.truckFinalizedDate || STATUSES_AT_OR_PAST_TRUCK_REPAIRED.includes(status);
  const reinspectionDone  = !!merged.readyForInspectionDate || STATUSES_AT_OR_PAST_REINSPECTION.includes(status);
  const proformaDone      = !!merged.proformaApprovedDate ||  !!merged.proformaDoc || ["PROFORMA_RECEIVED","ASSESSMENT_RECEIVED","ASSESSMENT_CROSSCHECKED","TAX_INVOICE_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(status);
  const assessmentReceived   = !!merged.assessmentReceivedDate  || ["ASSESSMENT_RECEIVED","ASSESSMENT_CROSSCHECKED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(status);
  const assessmentCrossChecked = !!merged.assessmentCrossCheckedDate  || ["ASSESSMENT_CROSSCHECKED","PAYMENT_REQUEST_SENT", "PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(status);
  const taxInvoiceReceived   = !!merged.taxInvoiceDoc  || ["TAX_INVOICE_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(status);
  const paymentsReceived = ["PAYMENT_COMPLETED", "CLAIM_SETTLED", "CASE_CLOSED"].includes(status);
  const claimSettled = ["CLAIM_SETTLED", "CASE_CLOSED"].includes(status);
  const caseClosed   = status === "CASE_CLOSED";
 
  // ── Current action & active phase ────────────────────────────────────────
  const currentAction = STATUS_TO_ACTION[status] || (
    !estimateApproved       ? "estimate"
    : !claimSent            ? "claim"
    : !surveyorAssigned     ? "surveyor"
    : !surveyDone           ? "survey_done"     
    : !dismantlingApproved  ? "dismantling_approval"
    : !dismantlingCompleted ? "dismantling_done"
    : !visit2Assigned       ? "second_visit"
    : !survey2Done          ? "second_survey"
    : !finalApproved        ? "final_approval"
    : !workStarted          ? "work_started"
    : !truckRepaired        ? "truck_repaired"
    : !reinspectionDone     ? "reinspection"
    : !proformaDone          ? "proforma"
    : !assessmentReceived    ? "assessment"
    : !assessmentCrossChecked ? "crosscheck"
    : !taxInvoiceReceived    ? "tax_invoice"
    : !paymentsDone          ? "payments"  
    : !paymentsReceived      ? "payments_received"   // ← add
    : !claimSettled          ? "settle_claim"         // ← add
    : !caseClosed            ? "close_case"       
    : "complete"
  );
 
  const activePhase = derivePhase({ estimateApproved, claimSent, surveyorAssigned,surveyDone, truckRepaired, reinspectionDone, proformaDone });
 
  // Selected tab — defaults to activePhase, user can click any tab freely
  const [selectedTab, setSelectedTab] = useState(activePhase);
  useEffect(() => { setSelectedTab(activePhase); }, [activePhase]);

  console.log(selectedTab, activePhase, currentAction);

  if (!workshopInfo?.workshopId) {
    return (
      <div className="wd-card" style={{ padding: 32 }}>
        <PendingSection
          icon="🔧"
          message="No workshop assigned yet"
          subtext={isBeforeWorkshop
            ? "Workshop assignment happens after the spot survey is completed."
            : "Survey completed. You can now assign a workshop from the Workshops page."}
          badge={isBeforeWorkshop ? "Waiting for survey" : "Ready to assign"}
          badgeColor={isBeforeWorkshop ? "var(--wd-orange)" : "var(--wd-blue)"}
        />
      </div>
    );
  }
 
  // ── Step definitions per phase ────────────────────────────────────────────
  const phaseSteps = {
    intake: [
      {
        key: "estimate",
        label: "Workshop intake & estimate",
        done: estimateApproved,
        component: (
          <StepWorkshopEstimate
            caseInfo={merged}
            workshopInfo={mergedWorkshopInfo}
            onRefetch={handleRefetch}
          />
        ),
      },
    ],

    claim: [
      {
        key: "claim",
        label: "Claim form",
        done: claimSent,
        component: (
          <StepClaimForm
            caseInfo={merged}
            workshopInfo={mergedWorkshopInfo}
            insuranceDetails={insuranceDetails}
            onRefetch={handleRefetch}
          />
        ),
      },
      {
        key: "surveyor",
        label: "Workshop surveyor assigned",
        done: surveyorAssigned,
        component: (
          <StepAssignWorkshopSurveyor
            caseInfo={merged}
            workshopSurvey={workshopSurveyor}
            onRefetch={handleRefetch}
            onGoToSurveys={onGoToSurveys}
            readOnly={surveyorAssigned}
          />
        ),
      },
      {
        key: "survey_done",
        label: "Workshop survey completed",
        done: surveyDone,
        component: (
          <StepAwaitWorkshopSurvey
            caseInfo={merged}
            workshopSurvey={workshopSurveyor}
            onGoToSurveys={onGoToSurveys}
          />
        ),
      },
    ],

    repair: [
      {
        key: "dismantling_approval",
        label: "Dismantling approved",
        done: dismantlingApproved,
        component: (
          <StepDismantlingApproval
            caseInfo={merged}
            onRefetch={handleRefetch}
          />
        ),
      },
      {
        key: "dismantling_done",
        label: "Dismantling completed",
        done: dismantlingCompleted,
        component: (
          <StepDismantlingCompleted
            caseInfo={merged}
            onRefetch={handleRefetch}
          />
        ),
      },
      {
        key: "second_visit",
        label: "2nd visit assigned",
        done: visit2Assigned,
        component: (
          <StepSecondVisit
            caseInfo={merged}
            workshopSurveyor={workshopSurveyor}
            onRefetch={handleRefetch}
            readOnly={visit2Assigned}
          />
        ),
      },
      {
        key: "second_survey",
        label: "2nd survey done",
        done: survey2Done,
        component: (
          <>
            <StepRevisedEstimate caseInfo={merged} onRefetch={handleRefetch} />
            <div style={{ height: 8 }} />
            <StepSecondSurveyDone
              caseInfo={merged}
              onRefetch={handleRefetch}
              readOnly={survey2Done}
            />
          </>
        ),
      },
      {
        key: "final_approval",
        label: "Final approval",
        done: finalApproved,
        component: (
          <StepFinalApproval
            caseInfo={merged}
            onRefetch={handleRefetch}
            readOnly={finalApproved}
          />
        ),
      },
      {
        key: "work_started",
        label: "Work started",
        done: workStarted,
        component: (
          <StepWorkStarted
            caseInfo={merged}
            onRefetch={handleRefetch}
            readOnly={workStarted}
          />
        ),
      },
      {
        key: "truck_repaired",
        label: "Truck repaired",
        done: truckRepaired,
        component: (
          <StepTruckRepaired
            caseInfo={merged}
            workshopSurveyor={workshopSurveyor}
            onRefetch={handleRefetch}
            readOnly={truckRepaired}
          />
        ),
      },
      {
        key: "reinspection",
        label: "Reinspection done",
        done: reinspectionDone,
        component: (
          <StepReinspection
            caseInfo={merged}
            onRefetch={handleRefetch}
            readOnly={reinspectionDone}
          />
        ),
      },
    ],

    delivery: [
      {
        key: "proforma",
        label: "Proforma invoice",
        done: proformaDone,
        component: (
          <StepProforma
            caseInfo={merged}
            onRefetch={handleRefetch}
            readOnly={proformaDone}
          />
        ),
      },
      {
        key: "assessment",
        label: "Surveyor assessment received",
        done: assessmentReceived,
        component: (
          <StepAssessment
            caseInfo={merged}
            onRefetch={handleRefetch}
            readOnly={assessmentReceived}
          />
        ),
      },
      {
        key: "crosscheck",
        label: "Assessment cross-checked",
        done: assessmentCrossChecked,
        component: (
          <StepCrossCheck
            caseInfo={merged}
            onRefetch={handleRefetch}
            readOnly={assessmentCrossChecked}
          />
        ),
      },
      {
        key: "tax_invoice",
        label: "Tax invoice received",
        done: taxInvoiceReceived,
        component: (
          <StepTaxInvoice
            caseInfo={merged}
            onRefetch={handleRefetch}
            readOnly={taxInvoiceReceived}
          />
        ),
      },
      {
        key: "payments",
        label: "Payments & settlement",
        done: paymentsDone,
        component: (
          <StepPaymentsSettlement caseInfo={merged} readOnly={paymentsDone} />
        ),
      },
      {
        key: "payments_received",
        label: "Payments received",
        done: paymentsReceived,
        component: (
          <StepMarkPaymentsReceived
            caseInfo={merged}
            onRefetch={handleRefetch}
          />
        ),
      },
      {
        key: "settle_claim",
        label: "Claim settled",
        done: claimSettled,
        component: (
          <StepSettleClaim caseInfo={merged} onRefetch={handleRefetch} />
        ),
      },
      {
        key: "close_case",
        label: "Case closed",
        done: caseClosed,
        component: (
          <StepCloseCase caseInfo={merged} onRefetch={handleRefetch} />
        ),
      },
    ],
  };
 
  const phases = [
    { key: "intake",   label: "Intake"         },
    { key: "claim",    label: "Claim & survey" },
    { key: "repair",   label: "Repair"         },
    { key: "delivery", label: "Delivery"       },
  ];
  const activePhaseIdx = PHASE_ORDER.indexOf(activePhase);
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
 
      {/* Workshop info strip */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
        background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
        borderRadius: 10, marginBottom: 12, overflow: "hidden",
      }}>
        {[
          ["Workshop NAME",     mergedWorkshopInfo.workshopName, true],
          ["Assigned date",   formatDateTime(mergedWorkshopInfo.workshopAssignedDate), false],
          ["Estimate amount", mergedWorkshopInfo.estimateAmount ? formatINR(mergedWorkshopInfo.estimateAmount) : null, false],
        ].map(([label, val, mono], i) => (
          <div key={label} style={{
            padding: "10px 14px",
            borderRight: i < 2 ? "1px solid var(--wd-border)" : "none",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--wd-t3)", marginBottom: 3 }}>{label}</div>
            {val
              ? <div style={{ fontSize: 12, fontWeight: 500, color: "var(--wd-t1)", fontFamily: mono ? "var(--wd-mono)" : "inherit" }}>{val}</div>
              : <div style={{ fontSize: 12, color: "var(--wd-t3)", fontStyle: "italic" }}>—</div>
            }
          </div>
        ))}
      </div>
 
      {/* ── Clickable phase tabs ──────────────────────────────────────────── */}
      <div style={{
        display: "flex", borderRadius: 8, overflow: "hidden",
        border: "1px solid var(--wd-border)", marginBottom: 14,
      }}>
        {phases.map((p, i) => {
          const phaseIdx   = PHASE_ORDER.indexOf(p.key);
          const isDone     = phaseIdx < activePhaseIdx;
          const isActive   = p.key === activePhase;
          const isSelected = p.key === selectedTab;

          console.log({ p: p.key, isDone, isActive, isSelected });
 
          return (
            <button
              key={p.key}
              onClick={() => setSelectedTab(p.key)}
              style={{
                flex: 1, textAlign: "center", padding: "8px 6px",
                fontSize: 10, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: ".04em",
                cursor: "pointer", border: "none",
                borderLeft: i > 0 ? "1px solid var(--wd-border)" : "none",
                borderBottom: isSelected ? "2px solid var(--wd-blue)" : "2px solid transparent",
                background: isSelected
                  ? "rgba(59,130,246,.09)"
                  : isDone    ? "rgba(34,197,94,.05)"
                  : isActive  ? "rgba(234,179,8,.05)"
                  : "transparent",
                color: isSelected
                  ? "var(--wd-blue)"
                  : isDone   ? "var(--wd-green)"
                  : isActive ? "var(--wd-yellow)"
                  : "var(--wd-t3)",
                transition: "background .12s, color .12s",
              }}
            >
              {isDone && !isSelected && (
                <span style={{ marginRight: 3 }}>✓</span>
              )}
              {isActive && !isSelected && (
                <span style={{
                  display: "inline-block", width: 5, height: 5,
                  borderRadius: "50%", background: "var(--wd-yellow)",
                  marginRight: 5, verticalAlign: "middle", marginBottom: 1,
                }} />
              )}
              {p.label}
            </button>
          );
        })}
      </div>
 
      {/* Main layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 14, alignItems: "start" }}>
 
      {selectedTab === "delivery" && assessmentReceived && 
   !assessmentCrossChecked && !taxInvoiceReceived ? (
    // Parallel steps — show side by side
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      
      {/* Completed within delivery */}
      {phaseSteps.delivery.filter(s => s.done && 
        !["crosscheck","tax_invoice"].includes(s.key)).map(s => (
        <CompletedRow key={s.key} label={s.label}>{s.component}</CompletedRow>
      ))}

      <SLabel>Action required — complete both to proceed</SLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <StepCrossCheck 
          caseInfo={merged} 
          onRefetch={handleRefetch}
          readOnly={assessmentCrossChecked} 
        />
        <StepTaxInvoice 
          caseInfo={merged} 
          onRefetch={handleRefetch}
          readOnly={taxInvoiceReceived} 
        />
      </div>
    </div>
  ) : (
    <PhaseContent
      phaseKey={selectedTab}
      activePhase={activePhase}
      currentAction={currentAction}
      steps={phaseSteps[selectedTab] || []}
      upNextItems={UP_NEXT[currentAction]}
    />
  )}
 
        {/* RIGHT: repair timeline */}
        <RepairTimeline caseInfo={merged} workshopSurveyor={workshopSurveyor} paymentsDone={paymentsDone}
          paymentsReceived={paymentsReceived}
          claimSettled={claimSettled}
          caseClosed={caseClosed}
        />
      </div>
    </div>
  );
}
